// The two fee-shape decisions the regenerate-plan modal has to get right.
//
// A flat fee exists to be a FIXED number. A percentage is re-derived from live debt whenever the
// debts change, so correcting a balance later would silently move a fee the client already signed.
// Both functions here protect that promise from the UI side — see feeType.js on the server for the
// same argument at the storage layer.

const PERCENT = 'percent'
const FLAT = 'flat'

/**
 * What to restore into the form when a saved plan is re-opened.
 *
 * The modal hydrated programLength, programFee, custodialFee and seven other fields from the saved
 * schedule, and never these two. So a flat client re-opened showing "Percentage" with no amount,
 * and saving sent `feeType: undefined` — which the server reads as a deliberate percentage pick
 * (resolveRequestedFeeShape defaults to percent by design). The client was converted to a
 * percentage of their debt, with a green success toast and nothing to see.
 *
 * The SHAPE decides; a leftover amount on a percentage schedule is not evidence of anything and is
 * dropped, so the two can never disagree about what was agreed.
 */
const restoreFeeShape = (schedule) => {
  const isFlat = schedule?.feeType === FLAT

  return {
    feeType: isFlat ? FLAT : PERCENT,
    flatFee: isFlat ? (schedule?.flatFee ?? null) : null,
  }
}

/**
 * Why Calculate may not proceed, or null when it may.
 *
 * Gating on the percentage unconditionally is what blocked the feature entirely: a flat client
 * never sets one, `programFee` stays '' and `'' < 1` is true in JavaScript, so the button was
 * clickable and then refused. Each shape is judged on the field it actually uses.
 *
 * The percentage message is unchanged, because operators know it.
 */
const feeAmountError = ({ isFlat, programFee, flatFee, typed = false } = {}) => {
  if (isFlat) {
    if (Number(flatFee) > 0) return null
    // The words follow the control: the typed field (WS #1944843) hands an invalid entry over as null.
    return typed ? 'Enter a flat fee amount' : 'Select a flat fee amount'
  }

  const percent = Number(programFee)
  const valid = programFee !== '' && programFee !== null && programFee !== undefined
    && !Number.isNaN(percent) && percent >= 1 && percent <= 50

  return valid ? null : 'Program Fee must be greater than 0 and not greater than 50'
}

module.exports = { restoreFeeShape, feeAmountError, PERCENT, FLAT }
